/**
 * באנר הסכמת עוגיות — רצועה תחתונה קבועה שמוצגת עד שהמשתמש בוחר.
 *
 * ⭐ דחייה חייבת להיות קלה כמו קבלה — דרישה של הנחיות הרשות להגנת הפרטיות.
 *    לכן "קבלת הכל" ו"עוגיות חיוניות בלבד" שניהם `.btn btn-cta` (זהים
 *    ויזואלית), ⛔ לא דחייה כקישור/כפתור משני. "התאמה אישית" (`.btn-secondary`)
 *    אינה החלטה אלא "הצג לי אפשרויות", ולכן מותר לה להיות משנית.
 *
 * ⛔ אין כאן מלכודת פוקוס — זו `region` לא-מודאלית (הפאנל המלא,
 *    `CookieConsentManager`, כן מודאלי). קורא מסך מגיע אליה דרך ניווט
 *    landmarks לפי ה-`aria-label`.
 *
 * ⭐ הכפתורים הצפים (`.a11y-fab` / `.wa-fab`) עולים מעל הבאנר כל עוד הוא
 *    מוצג — `document.body[data-consent-banner]` + `--consent-banner-h`
 *    (הגובה בפועל, נמדד), ⛔ לא ערך קבוע שינחש שגוי כשהטקסט נשבר לשורות.
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from './CookieConsentContext.js';
import { CookieConsentManager } from './CookieConsentManager.js';

export function CookieConsentBanner() {
  const { hasDecided, isManagerOpen, acceptAll, rejectAll, openManager } = useCookieConsent();
  const bannerRef = useRef<HTMLElement>(null);

  const show = !hasDecided && !isManagerOpen;

  useEffect(() => {
    if (!show) return;
    const body = document.body;

    function syncHeight() {
      const el = bannerRef.current;
      if (el) body.style.setProperty('--consent-banner-h', `${el.offsetHeight}px`);
    }

    syncHeight();
    body.setAttribute('data-consent-banner', 'open');
    window.addEventListener('resize', syncHeight);

    return () => {
      window.removeEventListener('resize', syncHeight);
      body.removeAttribute('data-consent-banner');
      body.style.removeProperty('--consent-banner-h');
    };
  }, [show]);

  return (
    <>
      {show && (
        <section ref={bannerRef} className="consent-banner" aria-label="הסכמה לשימוש בעוגיות">
          <div className="consent-banner-inner">
            <p className="consent-banner-text">
              אתר זה משתמש בעוגיות חיוניות הדרושות לתפעולו. בכפוף להסכמתך, נשתמש גם בעוגיות אנליטיקה
              ופרסום כדי לשפר את השירות ולמדוד קמפיינים. אפשר לשנות את הבחירה בכל עת. פרטים מלאים ב
              <Link to="/privacy" className="text-link underline">
                מדיניות הפרטיות
              </Link>
              .
            </p>
            <div className="consent-banner-actions">
              <button type="button" className="btn btn-cta" onClick={acceptAll}>
                קבלת הכל
              </button>
              <button type="button" className="btn btn-cta" onClick={rejectAll}>
                עוגיות חיוניות בלבד
              </button>
              <button type="button" className="btn btn-secondary" onClick={openManager}>
                התאמה אישית
              </button>
            </div>
          </div>
        </section>
      )}
      <CookieConsentManager />
    </>
  );
}
